import { Button, Grid, TextField, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { MobileDateTimePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AxiosError } from "axios";
import { useCallback, useContext, useEffect, useState } from "react";
import { UserContext } from "../../../../context/UserContext";
import { DbUser, UserCtx } from "../../../../interfaces";
import axios from "../../../../services/axios";
import { v2theme } from "../../../../styles/Theme";
import { AlertState } from "../../../../types";
import { mstDateKey } from "../../../utils/MSTDateTimeFormatter";
import { UserManagementModal } from "../UserManagementModal";

type AddMaintenanceBannerProps = {
  open: boolean;
  onClose: () => void;
  currentUser: DbUser;
  onSubmit: (alert: AlertState) => void;
};

type BannerFormState = {
  message: string;
  startDateTime: string;
  endDateTime: string;
};

type MaintenanceBannerResponse = {
  id: number;
  banner_from: string;
  banner_to: string;
  custom_banner_message: string | null;
};

const initBannerFormState: BannerFormState = {
  message: "",
  startDateTime: "",
  endDateTime: "",
};

// "YYYY-MM-DDTHH:mm" → Date (local time)
function toDate(internal: string): Date | null {
  if (!internal) return null;
  const d = new Date(internal);
  return isNaN(d.getTime()) ? null : d;
}

// Date → "YYYY-MM-DDTHH:mm"
function fromDate(d: Date): string {
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const h = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${y}-${mo}-${day}T${h}:${mi}`;
}

const useStyle = makeStyles((theme) => ({
  inputText: {
    "& .MuiOutlinedInput-root": {
      "&:hover fieldset": {
        borderColor: v2theme.palette.primary.main,
      },
      "&.Mui-focused fieldset": {
        borderColor: v2theme.palette.primary.main,
      },
    },
  },
}));

export function AddMaintenanceBanner(props: AddMaintenanceBannerProps) {
  const classes = useStyle();
  const { getFeatureFlagsAndMaintenance } = useContext(UserContext) as UserCtx;
  const [form, setForm] = useState<BannerFormState>(initBannerFormState);
  const [hasExistingEntry, setHasExistingEntry] = useState(false);
  const [removeLoading, setRemoveLoading] = useState(false);

  const resetForm = useCallback(() => {
    setForm(initBannerFormState);
  }, []);

  const updateForm = useCallback((update: Partial<BannerFormState>) => {
    setForm((prev) => ({ ...prev, ...update }));
  }, []);

  // Convert UTC → "YYYY-MM-DDTHH:mm" in Arizona time for datetime-local input
  const toDateTimeLocalValue = (dateLike: string) => {
    const date = new Date(dateLike);
    if (Number.isNaN(date.getTime())) return "";
    const datePart = mstDateKey(date); // "YYYY-MM-DD" in MST via MSTDateTimeFormatter
    const timePart = date.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      timeZone: "America/Phoenix",
    }); // "HH:mm" in MST
    return `${datePart}T${timePart}`;
  };

  useEffect(() => {
    if (!props.open) return;

    axios
      .get<MaintenanceBannerResponse | Record<string, never>>("/maintenance-banner/1")
      .then((res) => {
        const data = res.data as Partial<MaintenanceBannerResponse>;
        if (!data?.banner_from || !data?.banner_to) {
          setHasExistingEntry(false);
          resetForm();
          return;
        }
        setHasExistingEntry(true);
        setForm({
          message: data.custom_banner_message ?? "",
          startDateTime: toDateTimeLocalValue(data.banner_from),
          endDateTime: toDateTimeLocalValue(data.banner_to),
        });
      })
      .catch(() => {
        setHasExistingEntry(false);
        resetForm();
      });
  }, [props.open, resetForm]);

  const submit = async () => {
    const payload = {
      id: 1,
      // Treat entered time as Arizona MST (UTC-7) before converting to UTC
      banner_from: form.startDateTime
        ? new Date(form.startDateTime + "-07:00").toISOString()
        : new Date().toISOString(),
      banner_to: form.endDateTime
        ? new Date(form.endDateTime + "-07:00").toISOString()
        : new Date().toISOString(),
      custom_banner_message: form.message || null,
    };

    try {
      await axios.post("/maintenance-banner", payload);
      await getFeatureFlagsAndMaintenance();
      props.onSubmit({
        severity: "success",
        message: "Maintenance banner updated.",
      });
      setHasExistingEntry(true);
      resetForm();
    } catch (e) {
      if (e instanceof AxiosError) {
        props.onSubmit({
          severity: "error",
          message:
            "Unable to save maintenance banner right now. Please try again in a moment.",
        });
      }
      throw e;
    }
  };

  const handleClose = () => {
    resetForm();
    props.onClose();
  };

  const removeBannerMessage = async () => {
    try {
      setRemoveLoading(true);
      await axios.delete("/maintenance-banner/1");
      await getFeatureFlagsAndMaintenance();
      setHasExistingEntry(false);
      resetForm();
      props.onSubmit({
        severity: "success",
        message: "Maintenance banner removed.",
      });
      props.onClose();
    } catch (e) {
      if (e instanceof AxiosError) {
        props.onSubmit({
          severity: "error",
          message:
            "Unable to remove maintenance banner right now. Please try again in a moment.",
        });
      }
      throw e;
    } finally {
      setRemoveLoading(false);
    }
  };

  return (
    <UserManagementModal
      title="Add maintenance banner"
      open={props.open}
      onClose={handleClose}
      onSave={submit}>
      <Typography color="textSecondary" sx={{ marginBottom: "32px" }}>
        Configure banner content, visibility, and schedule.
      </Typography>
      <Grid container spacing={6}>
        <Grid item xs={12}>
          <TextField
            className={classes.inputText}
            label="Banner Message"
            value={form.message}
            onChange={(e) => updateForm({ message: e.target.value })}
            multiline
            minRows={3}
            fullWidth
          />
        </Grid>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Grid item xs={12} sm={6}>
            <MobileDateTimePicker
              label="Start Date & Time"
              value={toDate(form.startDateTime)}
              onChange={(val: Date | null) =>
                updateForm({ startDateTime: val ? fromDate(val) : "" })
              }
              views={["year", "month", "day", "hours", "minutes"]}
              inputFormat="MM/dd/yy hh:mm aa"
              renderInput={(params) => (
                <TextField
                  {...params}
                  className={classes.inputText}
                  helperText="MM/DD/YY, Arizona Time (MST)"
                  fullWidth
                />
              )}
              DialogProps={{
                sx: {
                  "& .MuiDialogActions-root .MuiButton-root": {
                    color: "#0092E0",
                  },
                },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <MobileDateTimePicker
              label="End Date & Time"
              value={toDate(form.endDateTime)}
              onChange={(val: Date | null) =>
                updateForm({ endDateTime: val ? fromDate(val) : "" })
              }
              views={["year", "month", "day", "hours", "minutes"]}
              inputFormat="MM/dd/yy hh:mm aa"
              renderInput={(params) => (
                <TextField
                  {...params}
                  className={classes.inputText}
                  helperText="MM/DD/YY, Arizona Time (MST)"
                  fullWidth
                />
              )}
              DialogProps={{
                sx: {
                  "& .MuiDialogActions-root .MuiButton-root": {
                    color: "#0092E0",
                  },
                },
              }}
            />
          </Grid>
        </LocalizationProvider>
        {hasExistingEntry && (
          <Grid item xs={12} sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="outlined"
              onClick={removeBannerMessage}
              disabled={removeLoading}>
              Remove Banner Message
            </Button>
          </Grid>
        )}
      </Grid>
    </UserManagementModal>
  );
}
